import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React from "react";
import axios from "axios";

interface Props {
  onResult: (result: any, ad: any) => void;
}

type Level = "advertisers" | "orders" | "lineitems" | "creatives";

interface Breadcrumb {
  level: Level;
  label: string;
  id?: number;
}

function apiUrl(path: string): string {
  const host = window.location.hostname;
  const port = window.location.port;
  const base = port ? `//${host}:${port}` : `//${host}`;
  return `${base}/copilot/genesys/adeval${path}`;
}

const AdevalBrowse = ({ onResult }: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [level, setLevel] = React.useState<Level>("advertisers");
  const [data, setData] = React.useState<any>(null);
  const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([]);
  const [scoring, setScoring] = React.useState(false);

  // Autocomplete state
  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load top advertisers on mount
  React.useEffect(() => {
    loadTopAdvertisers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced typeahead search
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!inputValue.trim()) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(apiUrl(`/browse/advertisers?q=${encodeURIComponent(inputValue.trim())}&limit=15`));
        setSuggestions(res.data?.advertisers || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const loadTopAdvertisers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(apiUrl("/browse/advertisers?limit=30"));
      setData(res.data);
      setLevel("advertisers");
      setBreadcrumbs([]);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to load advertisers");
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async (advertiserId: number, advertiserName: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(apiUrl(`/browse/advertisers/${advertiserId}/orders`));
      setData(res.data);
      setLevel("orders");
      setBreadcrumbs([{ level: "advertisers", label: advertiserName, id: advertiserId }]);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const loadLineItems = async (orderId: number, orderName: string, crumbs: Breadcrumb[]) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(apiUrl(`/browse/orders/${orderId}/lineitems`));
      setData(res.data);
      setLevel("lineitems");
      setBreadcrumbs([...crumbs, { level: "orders", label: orderName, id: orderId }]);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to load line items");
    } finally {
      setLoading(false);
    }
  };

  const loadCreatives = async (lineItemId: number, lineItemName: string, crumbs: Breadcrumb[]) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(apiUrl(`/browse/lineitems/${lineItemId}/creatives`));
      setData(res.data);
      setLevel("creatives");
      setBreadcrumbs([...crumbs, { level: "lineitems", label: lineItemName, id: lineItemId }]);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to load creatives");
    } finally {
      setLoading(false);
    }
  };

  const loadAdvertiserCreatives = async (advertiserId: number, crumbs: Breadcrumb[]) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(apiUrl(`/browse/advertisers/${advertiserId}/creatives`));
      setData(res.data);
      setLevel("creatives");
      setBreadcrumbs([...crumbs, { level: "creatives", label: "All Creatives" }]);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to load creatives");
    } finally {
      setLoading(false);
    }
  };

  const handleScoreCreative = async (creative: any) => {
    setScoring(true);
    setError("");
    try {
      const payload = {
        imageUrl: creative.imageUrl,
        vertical: "",
        adSize: creative.adSize || "300x250",
        brandName: creative.brandName || creative.name || "",
        model: "gpt-4o",
        temperature: 0.2,
        refLimit: 5,
      };
      const res = await axios.post(apiUrl("/score/withrefs"), payload);
      const ad = {
        campaignName: creative.brandName || creative.name || `Creative ${creative.creativeId}`,
        bgImageUrl: creative.imageUrl,
        vertical: "",
        adSize: creative.adSize,
      };
      onResult(res.data, ad);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Scoring failed");
    } finally {
      setScoring(false);
    }
  };

  const goBack = () => {
    if (breadcrumbs.length === 0) return;
    const newCrumbs = [...breadcrumbs];
    const last = newCrumbs.pop()!;

    if (last.level === "advertisers") {
      loadTopAdvertisers();
    } else if (last.level === "orders" && newCrumbs.length > 0) {
      const adv = newCrumbs[0];
      loadOrders(adv.id!, adv.label);
      setBreadcrumbs([]);
    } else if (last.level === "lineitems" && newCrumbs.length >= 1) {
      const adv = newCrumbs[0];
      if (newCrumbs.length >= 2) {
        const order = newCrumbs[1];
        loadLineItems(order.id!, order.label, [newCrumbs[0]]);
        setBreadcrumbs([newCrumbs[0]]);
      } else {
        loadOrders(adv.id!, adv.label);
        setBreadcrumbs([]);
      }
    } else if (last.level === "creatives" && newCrumbs.length >= 1) {
      const adv = newCrumbs[0];
      loadOrders(adv.id!, adv.label);
      setBreadcrumbs([]);
    } else {
      loadTopAdvertisers();
    }
  };

  return (
    <Box>
      {/* Autocomplete search */}
      <Autocomplete
        freeSolo
        options={suggestions}
        getOptionLabel={(opt: any) => (typeof opt === "string" ? opt : opt.name || "")}
        filterOptions={(x) => x}
        inputValue={inputValue}
        onInputChange={(_e, val) => setInputValue(val)}
        onChange={(_e, val) => {
          if (val && typeof val !== "string") {
            loadOrders(val.advertiserId, val.name);
          }
        }}
        loading={searchLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Advertisers"
            placeholder="Start typing... e.g. toyota"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchLoading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option: any) => (
          <li {...props} key={option.advertiserId}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                {option.activeBanners ?? option.bannerCount} banners
              </Typography>
            </Box>
          </li>
        )}
        sx={{ mb: 2 }}
      />

      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <IconButton size="small" onClick={goBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Chip
            label="Advertisers"
            size="small"
            variant="outlined"
            onClick={loadTopAdvertisers}
            sx={{ cursor: "pointer" }}
          />
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              <Typography variant="body2" color="text.secondary">/</Typography>
              <Chip label={crumb.label} size="small" variant={i === breadcrumbs.length - 1 ? "filled" : "outlined"} />
            </React.Fragment>
          ))}
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {scoring && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Scoring creative... this takes 10-20 seconds
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Advertisers */}
      {level === "advertisers" && data?.advertisers && (
        <AdvertiserList
          advertisers={data.advertisers}
          onSelect={loadOrders}
          onViewAll={loadAdvertiserCreatives}
        />
      )}

      {/* Orders */}
      {level === "orders" && data?.orders && (
        <OrderList
          orders={data.orders}
          onSelect={(id, name) => loadLineItems(id, name, breadcrumbs)}
        />
      )}

      {/* Line Items */}
      {level === "lineitems" && data?.lineItems && (
        <LineItemList
          lineItems={data.lineItems}
          onSelect={(id, name) => loadCreatives(id, name, breadcrumbs)}
        />
      )}

      {/* Creatives */}
      {level === "creatives" && data?.creatives && (
        <CreativeGrid
          creatives={data.creatives}
          onScore={handleScoreCreative}
          scoring={scoring}
        />
      )}
    </Box>
  );
};

// --- Sub-components ---

const AdvertiserList = ({
  advertisers,
  onSelect,
  onViewAll,
}: {
  advertisers: any[];
  onSelect: (id: number, name: string) => void;
  onViewAll: (id: number, crumbs: Breadcrumb[]) => void;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {advertisers.length} advertisers with banner creatives
    </Typography>
    {advertisers.map((adv: any) => (
      <Box
        key={adv.advertiserId}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          mb: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => onSelect(adv.advertiserId, adv.name)}
      >
        <Box>
          <Typography variant="body1" fontWeight="medium">{adv.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {adv.activeBanners ?? adv.bannerCount} banners
            {adv.orderCount != null && ` / ${adv.orderCount} orders`}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll(adv.advertiserId, [{ level: "advertisers", label: adv.name, id: adv.advertiserId }]);
            }}
          >
            All Creatives
          </Button>
          <Button size="small" variant="text">
            Orders
          </Button>
        </Box>
      </Box>
    ))}
  </Box>
);

const OrderList = ({
  orders,
  onSelect,
}: {
  orders: any[];
  onSelect: (id: number, name: string) => void;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {orders.length} orders
    </Typography>
    {orders.length === 0 && (
      <Typography color="text.secondary">No orders found for this advertiser.</Typography>
    )}
    {orders.map((order: any) => (
      <Box
        key={order.orderId}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          mb: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => onSelect(order.orderId, order.name)}
      >
        <Box>
          <Typography variant="body1" fontWeight="medium">{order.name || `Order ${order.orderId}`}</Typography>
          <Typography variant="caption" color="text.secondary">
            {order.lineItemCount} line items
            {order.startDate && ` / ${order.startDate.slice(0, 10)}`}
            {order.endDate && ` - ${order.endDate.slice(0, 10)}`}
          </Typography>
        </Box>
        <Chip
          label={order.state}
          size="small"
          color={order.state === "active" ? "success" : "default"}
          variant="outlined"
        />
      </Box>
    ))}
  </Box>
);

const LineItemList = ({
  lineItems,
  onSelect,
}: {
  lineItems: any[];
  onSelect: (id: number, name: string) => void;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {lineItems.length} line items
    </Typography>
    {lineItems.length === 0 && (
      <Typography color="text.secondary">No line items found.</Typography>
    )}
    {lineItems.map((li: any) => (
      <Box
        key={li.lineItemId}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          mb: 0.5,
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => onSelect(li.lineItemId, li.name)}
      >
        <Box>
          <Typography variant="body1" fontWeight="medium">{li.name || `Line Item ${li.lineItemId}`}</Typography>
          <Typography variant="caption" color="text.secondary">
            {li.bannerCreatives} banners / {li.totalCreatives} total
            {li.startDate && ` / ${li.startDate.slice(0, 10)}`}
            {li.endDate && ` - ${li.endDate.slice(0, 10)}`}
          </Typography>
        </Box>
        <Chip
          label={li.state}
          size="small"
          color={li.state === "active" ? "success" : "default"}
          variant="outlined"
        />
      </Box>
    ))}
  </Box>
);

const CreativeGrid = ({
  creatives,
  onScore,
  scoring,
}: {
  creatives: any[];
  onScore: (creative: any) => void;
  scoring: boolean;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {creatives.length} banner creatives
    </Typography>
    {creatives.length === 0 && (
      <Typography color="text.secondary">No banner creatives found.</Typography>
    )}
    <Grid container spacing={2}>
      {creatives.map((cr: any) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={cr.creativeId}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "background.paper" }}>
            <Box sx={{ position: "relative" }}>
              {cr.imageUrl ? (
                <CardMedia
                  component="img"
                  height="180"
                  image={cr.imageUrl}
                  alt={cr.name}
                  sx={{ objectFit: "contain", bgcolor: "grey.900" }}
                />
              ) : (
                <Box sx={{ height: 180, bgcolor: "grey.800", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary" variant="body2">No image</Typography>
                </Box>
              )}
              <Chip
                label={cr.adSize}
                size="small"
                sx={{ position: "absolute", top: 8, left: 8, bgcolor: "rgba(0,0,0,0.7)", color: "#fff" }}
              />
              {cr.isExpired && (
                <Chip label="Expired" size="small" color="error" sx={{ position: "absolute", top: 8, right: 8 }} />
              )}
            </Box>
            <Box sx={{ p: 1.5, flexGrow: 1 }}>
              <Typography variant="body2" noWrap fontWeight="medium">{cr.name || `Creative ${cr.creativeId}`}</Typography>
              {cr.brandName && (
                <Typography variant="caption" color="text.secondary" display="block">{cr.brandName}</Typography>
              )}
              {cr.createdOn && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Created: {String(cr.createdOn).slice(0, 10)}
                </Typography>
              )}
            </Box>
            <Box sx={{ p: 1, pt: 0 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => onScore(cr)}
                disabled={!cr.imageUrl || scoring}
                fullWidth
              >
                Score This
              </Button>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default AdevalBrowse;
